<script lang="ts">
import type { UserVariableDefinitionDataType } from '@/types/schema.generated'

export type UserVariableDefinitionType = {
  dataType:
    | typeof UserVariableDefinitionDataType.MultipleSelect
    | typeof UserVariableDefinitionDataType.Select
  guid: string
  name: string
  options: {
    guid: string
    name: string
  }[]
}
export default {}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import ChatMessageSendingJobNewPageCsvTargetSetting from './ChatMessageSendingJobNewPageCsvTargetSetting.vue'
import SegmentForm from '@/features/segment/components/SegmentForm/SegmentForm.vue'
import type { SegmentItemType } from '@/features/segment/components/SegmentForm/SegmentFormItem.vue'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import type { ChatMessageSendingJobTarget } from '../types'
import {
  allChatRoomStatuses,
  chatRoomStatusString,
} from '@/models/chatRoom/chatRoomStatus'
import { ChatRoomStatus } from '@/types/schema.generated'
import ChatMessageSendingJobNewPageTargetSettingErrorMessage from './ChatMessageSendingJobNewPageTargetSettingErrorMessage.vue'

const model = defineModel<ChatMessageSendingJobTarget>({ required: true })

const props = defineProps<{
  userRegistrationInflowSourceDefinitions: {
    guid: string
    inflowSourceName: string
  }[]
  clickURLs: { guid: string; url: string }[]
  plannerEnquetes: { guid: string; title: string }[]
  userVariableDefinitions: UserVariableDefinitionType[]
  errorMessage?: string
}>()

const targetOptions = [
  {
    type: 'all',
    label: 'マッチしているすべてのユーザー',
    handler: () => {
      model.value = {
        type: 'all',
      }
    },
  },
  {
    type: 'csv',
    label: 'CSVで入力',
    handler: () => {
      model.value = {
        type: 'csv',
        okanentClientUserIds: [],
      }
    },
  },
  {
    type: 'segment',
    label: 'セグメント',
    handler: () => {
      model.value = {
        type: 'segment',
        segment: {
          segmentConditions: [],
        },
      }
    },
  },
] as const

const inflowSourceOptions = computed(() => {
  return props.userRegistrationInflowSourceDefinitions.map(source => ({
    text: source.inflowSourceName,
    value: source.guid,
  }))
})

const clickUrlOptions = computed(() => {
  return props.clickURLs.map(url => ({
    text: url.url,
    value: url.url,
  }))
})

const plannerEnqueteOptions = computed(() => {
  return props.plannerEnquetes.map(enquete => ({
    text: enquete.title,
    value: enquete.guid,
  }))
})

// 未設定は選択肢に含めない
const roomStatusOptions = allChatRoomStatuses
  .filter(status => status !== ChatRoomStatus.NotDefined)
  .map(status => ({
    text: chatRoomStatusString[status],
    value: status,
  }))

const userVariableDefinitionOptions = computed(() =>
  props.userVariableDefinitions.map(definition => ({
    text: definition.name,
    value: definition.guid,
    options: definition.options.map(option => ({
      text: option.name,
      value: option.guid,
    })),
    dataType: definition.dataType,
  }))
)

const segmentTypeOptions: {
  text: string
  value: SegmentItemType
}[] = [
  {
    text: 'ログイン連携',
    value: 'line-activation-login-link',
  },
  {
    text: '友だち追加経路',
    value: 'inflow-source',
  },
  {
    text: 'URLクリック',
    value: 'click-url',
  },
  {
    text: 'アンケート回答',
    value: 'planner-enquete-answered',
  },
  {
    text: '対応状況',
    value: 'room-status',
  },
  {
    text: 'カスタム項目',
    value: 'user-variable-definition',
  },
]
</script>

<template>
  <section>
    <p :class="$style.sectionTitle">ターゲット</p>
    <div :class="$style.sectionContent">
      <div :class="$style.optionsWrapper">
        <label
          v-for="target of targetOptions"
          :key="target.type"
          :class="$style.option"
        >
          <DolphinRadioButton
            name="target"
            :value="target.type"
            :modelValue="model.type"
            @update:modelValue="target.handler"
          />
          <span :class="$style.option__text">
            {{ target.label }}
          </span>
        </label>
      </div>
    </div>
    <ChatMessageSendingJobNewPageCsvTargetSetting
      v-if="model.type === 'csv'"
      v-model="model"
      :class="$style.customTargetSetting"
    />
    <SegmentForm
      v-if="model.type === 'segment'"
      v-model="model"
      :segmentTypeOptions="segmentTypeOptions"
      :inflowSourceOptions="inflowSourceOptions"
      :clickUrlOptions="clickUrlOptions"
      :plannerEnqueteOptions="plannerEnqueteOptions"
      :roomStatusOptions="roomStatusOptions"
      :userVariableDefinitionOptions="userVariableDefinitionOptions"
      :class="$style.customTargetSetting"
    />
    <ChatMessageSendingJobNewPageTargetSettingErrorMessage
      :errorMessage="props.errorMessage"
    />
  </section>
</template>

<style module lang="scss">
.sectionTitle {
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: bold;
}

.sectionContent {
  margin-top: 16px;
}

.optionsWrapper {
  display: flex;
  gap: 24px;
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.customTargetSetting {
  margin-top: 16px;
}
</style>
